// ThemeToggle — a small button that cycles System → Light → Dark and back.
//
// Single-button-cycle pattern instead of a dropdown — keeps the surface
// area trivial and works equally well in TopNav (post-auth) and the
// Landing header (pre-auth). The icon and aria-label reflect the CURRENT
// stored theme (system/light/dark), not the resolved one, so users
// understand which mode they're in vs what the system would default to.

import { useTheme, type Theme } from '../hooks/useTheme'

const ORDER: Theme[] = ['system', 'light', 'dark']
const LABEL: Record<Theme, string> = {
  system: 'System',
  light: 'Light',
  dark: 'Dark',
}

export default function ThemeToggle({ className = '' }: { className?: string }) {
  const { theme, setTheme } = useTheme()

  function next(): Theme {
    const idx = ORDER.indexOf(theme)
    return ORDER[(idx + 1) % ORDER.length]
  }

  return (
    <button
      type="button"
      onClick={() => void setTheme(next())}
      title={`Theme: ${LABEL[theme]}. Click for ${LABEL[next()]}.`}
      aria-label={`Theme: ${LABEL[theme]}. Click to switch to ${LABEL[next()]}.`}
      className={
        // Color-neutral: inherits currentColor from its parent so the same
        // button reads bone on the navy TopNav and navy/bone on the Landing
        // canvas. Subtle slate hover works on either background.
        'inline-flex items-center justify-center w-8 h-8 rounded-md text-current hover:bg-hoop-accent/10 transition-colors ' +
        className
      }
    >
      <ThemeIcon theme={theme} />
    </button>
  )
}

function ThemeIcon({ theme }: { theme: Theme }) {
  if (theme === 'light') return <SunIcon />
  if (theme === 'dark') return <MoonIcon />
  return <SystemIcon />
}

function SunIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  )
}

function MoonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  )
}

function SystemIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="4" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  )
}
